import React, { useState, useEffect } from "react";
import { Calendar, Wrench, Building, Clipboard, ChevronLeft, ChevronRight } from 'lucide-react';
import appointmentService from "../services/appointmentService";
import "../styles/appointments.css";

const AppointmentCalendar = ({ appointmentType = null }) => {
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedType, setSelectedType] = useState(appointmentType || "all"); // all, tech_lab, aksaray_building
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchCalendarData();
  }, [selectedType]);

  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      const typeFilter = selectedType === "all" ? null : selectedType;
      const response = await appointmentService.getCalendarData(typeFilter, token);
      setEvents(response.events || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Bir haftayı (7 gün) al
  const getWeekDays = () => {
    const days = [];
    const start = new Date(currentDate);
    // Haftanın Pazartesi'sine al
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1);
    start.setDate(diff);

    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      days.push(date);
    }
    return days;
  };

  // Yardımcı: saat sayısını al ("HH:mm" -> HH)
  const getHourNumber = (t) => {
    const [h] = String(t || "").split(":");
    const n = parseInt(h, 10);
    return isNaN(n) ? null : n;
  };

  // Saat aralığı: her zaman tam gün (06:00 - 23:00)
  const computeHourRange = () => {
    return { start: 6, end: 23 };
  };

  // Saatleri al (dinamik aralık)
  const getHours = () => {
    const { start, end } = computeHourRange();
    const hours = [];
    for (let i = start; i <= end; i++) {
      hours.push(`${String(i).padStart(2, "0")}:00`);
    }
    return hours;
  };

  // Belirli gün ve saat için etkinlikleri getir
  const getEventForTimeSlot = (date, hour) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    // Saat eşleştirmeyi dakika bağımsız yap (07:27 -> 07:00 slotunda görünür)
    const slotHour = getHourNumber(hour);
    return events.find((event) => {
      const eventHour = getHourNumber(event.time);
      return event.date === dateStr && eventHour !== null && eventHour === slotHour;
    });
  };

  // Gün adı ve tarihi formatla
  const formatDayHeader = (date) => {
    const dayName = date.toLocaleString("tr-TR", { weekday: "short" });
    const dateStr = date.toLocaleString("tr-TR", { 
      day: "2-digit", 
      month: "2-digit", 
      year: "numeric" 
    });
    return { dayName, dateStr };
  };

  const previousWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const nextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const weekDays = getWeekDays();
  const hours = getHours();

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <h3><Calendar className="inline-icon"/> Onaylanmış Randevular - Zaman Çizelgesi</h3>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="timeline-controls">
        <div className="facility-filter">
          <label htmlFor="facility-select">Tesis Seç:</label>
          <select
            id="facility-select"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="filter-select"
          >
            <option value="all">Tümü</option>
            <option value="tech_lab"><Wrench className="inline-icon"/> Teknoloji Atölyesi</option>
            <option value="aksaray_building"><Building className="inline-icon"/> Aksaray Hanımevi</option>
          </select>
        </div>
        <button onClick={previousWeek} className="btn btn-sm"><ChevronLeft className="inline-icon"/> Önceki Hafta</button>
        <span className="week-info">
          {weekDays[0].toLocaleDateString("tr-TR")} - {weekDays[6].toLocaleDateString("tr-TR")}
        </span>
        <button onClick={nextWeek} className="btn btn-sm">Sonraki Hafta <ChevronRight className="inline-icon"/></button>
      </div>

      {loading ? (
        <div className="loading">Yükleniyor...</div>
      ) : (
        <div className="timeline-schedule">
          {/* Saat sütunu */}
          <div className="time-column">
            <div className="time-header"></div>
            {hours.map((hour) => (
              <div key={hour} className="time-slot">
                {hour}
              </div>
            ))}
          </div>

          {/* Günler ve etkinlikler */}
          <div className="days-container">
            {weekDays.map((date, dayIndex) => {
              const { dayName, dateStr } = formatDayHeader(date);
              return (
                <div key={dayIndex} className="day-column">
                  <div className="day-header">
                    <div className="day-name">{dayName}</div>
                    <div className="day-date">{dateStr}</div>
                  </div>
                  <div className="day-events">
                    {hours.map((hour) => {
                      const event = getEventForTimeSlot(date, hour);
                      return (
                        <div
                          key={hour}
                          className={`event-slot ${event ? "has-event" : ""}`}
                        >
                          {event && (
                            <div className="event-item" title={event.title}>
                              <div className="event-time">{event.time}</div>
                              <div className="event-title">{event.title}</div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {events.length > 0 && (
        <div className="calendar-events-list">
          <h4><Clipboard className="inline-icon"/> Yaklaşan Randevular</h4>
          <div className="events-list-items">
            {events.slice(0, 10).map((event, idx) => (
              <div key={idx} className="event-list-item">
                <span className="event-date-time">{event.date} {event.time}</span>
                <span className="event-title">{event.title}</span>
                {event.facility_name && (
                  <span className="event-facility">{event.facility_name}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentCalendar;
